import React, { useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import { X, Eraser, Check, ChevronRight, PenLine } from 'lucide-react';

// Pass the tablet around: one person at a time signs the attendance sheet on screen.
export default function TrainingSignInModal({ attendees, signed, startId, signUrl, onSigned, onClose }) {
  const order = useMemo(() => {
    const list = [...attendees];
    if (startId) { const i = list.findIndex((a) => a.employee_id === startId); if (i > 0) list.unshift(...list.splice(i, 1)); }
    return list;
  }, [attendees, startId]);
  const [idx, setIdx] = useState(0);
  const [hasInk, setHasInk] = useState(false);
  const [busy, setBusy] = useState(false);
  const sig = useRef(null);
  const person = order[idx];
  const signedCount = order.filter((a) => signed[a.employee_id]).length;

  const clear = () => { sig.current?.clear(); setHasInk(false); };
  const go = (i) => { clear(); setIdx(i); };
  const next = () => {
    const after = order.findIndex((a, i) => i > idx && !signed[a.employee_id]);
    const any = order.findIndex((a) => !signed[a.employee_id]);
    if (after >= 0) go(after); else if (any >= 0 && any !== idx) go(any); else onClose();
  };
  const save = async () => {
    if (!sig.current || sig.current.isEmpty()) { toast.error('Sign first'); return; }
    setBusy(true);
    try {
      const r = await axios.post(signUrl, { employee_id: person.employee_id, signature: sig.current.getCanvas().toDataURL('image/png') });
      onSigned(r.data.signed);
      toast.success(`${person.name} signed in`);
      const after = order.findIndex((a, i) => i > idx && !r.data.signed[a.employee_id]);
      const any = order.findIndex((a) => !r.data.signed[a.employee_id]);
      if (after >= 0) go(after); else if (any >= 0) go(any); else { clear(); toast.success('Everyone has signed in'); onClose(); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save the signature'); }
    setBusy(false);
  };

  if (!person) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-slate-950/95 flex flex-col" data-testid="tc-signin-modal">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
        <p className="text-slate-700 text-sm flex items-center gap-2"><PenLine className="w-4 h-4 text-emerald-700" /> Sign-in sheet · <span data-testid="tc-signin-progress">{signedCount} of {order.length} signed</span></p>
        <button onClick={onClose} className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-50" data-testid="tc-signin-close"><X className="w-5 h-5" /></button>
      </div>
      <div className="flex gap-1.5 overflow-x-auto px-4 py-2 border-b border-slate-200" data-testid="tc-signin-people">
        {order.map((a, i) => (
          <button key={a.employee_id} onClick={() => go(i)} data-testid={`tc-signin-pick-${a.employee_id}`}
            className={`shrink-0 text-xs px-2.5 py-1 rounded-full border flex items-center gap-1 ${i === idx ? 'bg-white text-slate-900 border-white' : signed[a.employee_id] ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-white text-slate-700 border-slate-300'}`}>
            {signed[a.employee_id] && <Check className="w-3 h-3" />}{a.name.split(' ')[0]}
          </button>
        ))}
      </div>
      <div className="flex-1 flex flex-col items-center justify-center p-4 gap-4 max-w-3xl w-full mx-auto">
        <div className="text-center">
          <p className="text-slate-500 text-sm">Please pass the tablet to</p>
          <p className="text-slate-900 font-black text-3xl sm:text-4xl" data-testid="tc-signin-name">{person.name}</p>
          <p className="text-slate-500 text-xs mt-2 max-w-md mx-auto">By signing I confirm I attended the full session and completed the courses ticked beside my name.</p>
          {signed[person.employee_id] && <p className="text-emerald-700 text-xs mt-1" data-testid="tc-signin-already">Already signed — sign again to replace it</p>}
        </div>
        <div className="w-full bg-white rounded-2xl overflow-hidden border-4 border-dashed border-slate-500" data-testid="tc-signin-canvas">
          <SignatureCanvas ref={sig} penColor="#0b1f3a" canvasProps={{ className: 'w-full touch-none', style: { width: '100%', height: '260px' } }} onEnd={() => setHasInk(true)} />
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2 w-full">
          <button onClick={clear} className="px-4 py-3 rounded-xl text-sm bg-white border border-slate-300 text-slate-800 flex items-center gap-1.5" data-testid="tc-signin-clear"><Eraser className="w-4 h-4" /> Clear</button>
          <button onClick={next} className="px-4 py-3 rounded-xl text-sm bg-white border border-slate-300 text-slate-800 flex items-center gap-1.5" data-testid="tc-signin-skip">Skip <ChevronRight className="w-4 h-4" /></button>
          <button onClick={save} disabled={busy || !hasInk} className="flex-1 min-w-[200px] px-5 py-3 rounded-xl text-base font-bold bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-40 flex items-center justify-center gap-2" data-testid="tc-signin-save">
            <Check className="w-5 h-5" /> {busy ? 'Saving…' : 'Save & pass it on'}
          </button>
        </div>
      </div>
    </div>
  );
}
